import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crosshair, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SpotMap } from "../components/SpotMap";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Slider } from "../components/ui/slider";
import { Label } from "../components/ui/label";
import { api, errMsg } from "../lib/api";

const PRESETS = [0, 5, 10, 15, 30, 45, 60];

export default function SignalPage() {
  const nav = useNavigate();
  const [picked, setPicked] = useState(null);
  const [minutes, setMinutes] = useState(15);
  const [form, setForm] = useState({ address: "", vehicle: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [geo, setGeo] = useState({ loading: false, failed: false, hasNumber: true });
  const [flyTo, setFlyTo] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const reqId = useRef(0);
  const pick = async (pos) => {
    const id = ++reqId.current;
    setPicked(pos);
    setGeo({ loading: true, failed: false, hasNumber: true });
    try {
      const { data } = await api.get("/geocode/reverse", { params: { lat: pos[0], lng: pos[1] } });
      if (id !== reqId.current) return;
      setForm((f) => ({ ...f, address: data.address }));
      setGeo({ loading: false, failed: false, hasNumber: data.has_number });
    } catch (err) {
      if (id !== reqId.current) return;
      setForm((f) => ({ ...f, address: "" }));
      setGeo({ loading: false, failed: true, hasNumber: true });
      toast.error(`${errMsg(err)}. Puoi scrivere l'indirizzo a mano.`);
    }
  };

  const useGps = () => {
    if (!navigator.geolocation) return toast.error("Geolocalizzazione non disponibile");
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const pos = [p.coords.latitude, p.coords.longitude];
        setFlyTo(pos);
        pick(pos);
      },
      () => toast.error("Impossibile ottenere la posizione: controlla i permessi del browser"),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!picked) return toast.error("Tocca la mappa per indicare dove si trova il posto");
    if (geo.loading) return toast.error("Attendi il rilevamento dell'indirizzo");
    setBusy(true);
    try {
      await api.post("/spots", { lat: picked[0], lng: picked[1], minutes, ...form });
      toast.success("Posto segnalato! Riceverai una notifica se qualcuno lo prenota.");
      nav("/");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-[1.2fr_1fr] gap-8">
      <section className="rise">
        <p className="font-mono-x text-[11px] uppercase tracking-wider text-amber-400">Passo 1</p>
        <h2 className="font-display text-3xl font-extrabold mt-1">Dove hai parcheggiato?</h2>
        <p className="text-sm text-stone-400 mt-1">Tocca la mappa nel punto esatto del tuo posto auto.</p>
        <div className="relative mt-4 h-[360px] sm:h-[460px] rounded-2xl overflow-hidden border border-stone-800">
          <SpotMap picked={picked} onPick={pick} flyTo={flyTo} className="h-full w-full" />
          <button type="button" data-testid="signal-gps-button" onClick={useGps} className="absolute z-[500] bottom-8 left-3 h-10 px-4 rounded-full bg-stone-900/90 backdrop-blur border border-stone-700 text-sm inline-flex items-center gap-2 hover:border-amber-500 transition-colors">
            <Crosshair className="h-4 w-4 text-amber-400" /> La mia posizione
          </button>
        </div>
        <p data-testid="signal-picked-status" className="text-xs mt-2 text-stone-500">
          {picked ? `Posizione selezionata: ${picked[0].toFixed(5)}, ${picked[1].toFixed(5)}` : "Nessuna posizione selezionata"}
        </p>
      </section>

      <form onSubmit={submit} className="space-y-6 rise" style={{ animationDelay: "120ms" }} data-testid="signal-form">
        <div>
          <p className="font-mono-x text-[11px] uppercase tracking-wider text-amber-400">Passo 2</p>
          <h2 className="font-display text-3xl font-extrabold mt-1">Tra quanto parti?</h2>
          <p className="text-sm text-stone-400 mt-1">Puoi segnalarlo fino a 60 minuti prima.</p>
        </div>
        <div className="rounded-2xl bg-stone-900 border border-stone-800 p-5">
          <p data-testid="signal-minutes-value" className="font-display text-5xl font-extrabold">
            {minutes === 0 ? "Adesso" : `${minutes} min`}
          </p>
          <Slider data-testid="signal-minutes-slider" className="mt-5" min={0} max={60} step={1} value={[minutes]} onValueChange={(v) => setMinutes(v[0])} />
          <div className="flex flex-wrap gap-2 mt-4">
            {PRESETS.map((p) => (
              <button type="button" key={p} data-testid={`signal-preset-${p}`} onClick={() => setMinutes(p)}
                className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${minutes === p ? "bg-amber-500 text-stone-950 border-amber-500" : "border-stone-700 hover:border-amber-500"}`}>
                {p === 0 ? "Ora" : `${p}'`}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="address">Indirizzo {geo.failed ? "" : "(compilato automaticamente)"}</Label>
          <div className="relative">
            <Input id="address" data-testid="signal-address-input" required minLength={2} value={form.address} onChange={set("address")}
              readOnly={!geo.failed && geo.hasNumber}
              placeholder={geo.loading ? "Ricerca indirizzo..." : geo.failed ? "Scrivi via e civico" : "Usa 'La mia posizione' o tocca la mappa"}
              className={`h-12 bg-stone-900 border-stone-700 ${!geo.failed ? "cursor-default text-amber-100" : ""}`} />
            {geo.loading && <Loader2 data-testid="signal-address-loading" className="absolute right-3 top-3.5 h-5 w-5 animate-spin text-amber-400" />}
          </div>
          {picked && !geo.loading && !geo.failed && !geo.hasNumber && (
            <p data-testid="signal-address-no-number" className="text-xs text-stone-400">Civico non trovato sulla mappa per questo punto: se vuoi aggiungilo in fondo all'indirizzo.</p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="vehicle">La tua auto (facoltativo)</Label>
          <Input id="vehicle" data-testid="signal-vehicle-input" value={form.vehicle} onChange={set("vehicle")} placeholder="Es. Fiat Panda bianca" className="h-12 bg-stone-900 border-stone-700" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="note">Note (facoltativo)</Label>
          <Textarea id="note" data-testid="signal-note-input" value={form.note} onChange={set("note")} placeholder="Es. posto lungo, adatto anche a SUV" className="bg-stone-900 border-stone-700" />
        </div>
        <button data-testid="signal-submit-button" disabled={busy} className="w-full h-12 rounded-full bg-[#E0533C] hover:bg-[#C8432D] font-semibold text-white transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Segnala il posto
        </button>
      </form>
    </div>
  );
}
